import { useEffect, useState, type FormEvent } from "react"
import { Link } from "react-router-dom"
import { api, ApiError } from "../../lib/api"
import type { Chamado, ChamadoResumo } from "../../types"
import { dataCurta } from "../../lib/format"
import { Botao, Card, Carregando, Erro } from "../ui"
import { Situacao } from "./situacao"

const LIMITE_DA_MENSAGEM = 5000

export default function Suporte() {
  const [chamados, setChamados] = useState<ChamadoResumo[] | null>(null)
  const [assunto, setAssunto] = useState("")
  const [mensagem, setMensagem] = useState("")
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [enviado, setEnviado] = useState(false)

  useEffect(() => {
    api
      .get<{ chamados: ChamadoResumo[] }>("/chamados")
      .then((r) => setChamados(r.chamados))
      .catch(() => setChamados([]))
  }, [])

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (!assunto.trim() || !mensagem.trim()) return

    setEnviando(true)
    setErro(null)
    try {
      const { chamado } = await api.post<{ chamado: Chamado }>("/chamados", {
        chamado: { assunto, mensagem },
      })
      setChamados((anteriores) => [chamado, ...(anteriores ?? [])])
      setAssunto("")
      setMensagem("")
      setEnviado(true)
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não foi possível enviar. Tente de novo.")
    } finally {
      setEnviando(false)
    }
  }

  const campo = "w-full rounded-xl border border-navy-200 px-3.5 py-2.5 text-sm text-navy-900 outline-none focus:border-gold-500"

  return (
    <div className="space-y-8">
      <header className="revela">
        <h1 className="font-display text-3xl leading-none text-navy-900">Suporte</h1>
        <p className="mt-2 text-sm text-slate-600">
          Abra um chamado e acompanhe a resposta aqui mesmo — você também recebe por e-mail.
        </p>
      </header>

      {erro && <Erro mensagem={erro} />}

      <Card className="revela space-y-4">
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-navy-600">Abrir chamado</h2>
          {/* Dizer o que ajuda a resolver evita o chamado de uma linha só, que
              custa duas idas e voltas antes de virar resposta. */}
          <p className="mt-1 text-sm text-slate-600">
            Se for sobre pagamento, conte por onde pagou (Pix ou cartão) e quando — assim a gente já
            confere a compra antes de responder.
          </p>
        </div>

        <form onSubmit={enviar} className="space-y-3">
          <div>
            <label htmlFor="assunto" className="block text-sm font-semibold text-navy-900">
              Assunto
            </label>
            <input
              id="assunto"
              value={assunto}
              onChange={(e) => {
                setAssunto(e.target.value)
                setEnviado(false)
              }}
              maxLength={160}
              required
              placeholder="Paguei por Pix e o acesso não liberou"
              className={`mt-1 ${campo}`}
            />
          </div>

          <div>
            <label htmlFor="mensagem" className="block text-sm font-semibold text-navy-900">
              Mensagem
            </label>
            <textarea
              id="mensagem"
              value={mensagem}
              onChange={(e) => {
                setMensagem(e.target.value)
                setEnviado(false)
              }}
              maxLength={LIMITE_DA_MENSAGEM}
              required
              rows={6}
              placeholder="Descreva o que aconteceu."
              className={`mt-1 ${campo}`}
            />
            <p className="mt-1 text-right font-mono text-xs text-slate-400">
              {mensagem.length}/{LIMITE_DA_MENSAGEM}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Botao type="submit" disabled={enviando || !assunto.trim() || !mensagem.trim()}>
              {enviando ? "Enviando…" : "Enviar chamado"}
            </Botao>
            {enviado && <span className="text-sm text-emerald-700">Chamado aberto. Respondemos por e-mail.</span>}
          </div>
        </form>
      </Card>

      <section aria-label="Meus chamados" className="revela space-y-3">
        <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-navy-600">Meus chamados</h2>

        {chamados === null ? (
          <Carregando />
        ) : chamados.length === 0 ? (
          <Card>
            <p className="text-center text-sm text-slate-600">Você ainda não abriu nenhum chamado.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {chamados.map((chamado) => (
              <Link key={chamado.id} to={`/suporte/${chamado.id}`} className="block">
                <Card className="flex items-center justify-between gap-4 transition hover:border-navy-300">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-navy-900">{chamado.assunto}</p>
                    <p className="text-xs text-slate-500">{dataCurta(chamado.created_at)}</p>
                  </div>
                  <Situacao status={chamado.status} />
                </Card>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
